import React, { useState, useCallback, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { useModal } from 'react-modal-hook'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import NumberFormat from 'react-number-format'
import { NeoOTP, NeoSuccess } from '../../../components/FormComponents'
import NeoButton from '../../../components/NeoButton/NeoButton'
import NeoInput from '../../../components/NeoInput/NeoInput'
import InputError from '../../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
// import { AddInternalBeneficiarySchema } from '../../../../schemas/beneficiary-transfer'
import Modal from '../../../components/Modal/Modal'
import TermsAndCondition from '../../../components/TermsAndConditions/TermsAndCondition'
import LayoutWithAccountCard from '../../Layout/layout-account-card'
import { AddInternalBeneficiarySchema } from '../../../schemas/transfers'
import BeneficiaryService from '../../../services/beneficiary.service'
import { ADD_BENEFICIARY_KEYS, BUTTON_KEYS } from '../../../utils/translation-constants'
import validationConfig from 'custom/config/validation.config.json'
const internalBeneConfig = validationConfig['addBeneficiary']['internal']
import config from 'custom/config/environment'

export default function AddInternalBeneficiary() {
  const history = useHistory()
  const [stage, setStage] = useState(0)
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [formValues, setFormValues] = useState({
    nickName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    maxTransferAmount: '',
    transferAmount: undefined,
    isVisibleToGroup: false,
  })
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [otp, setOtp] = useState('')
  const [verifiedBeneDetails, setVerifiedBeneDetails] = useState({})
  const { t } = useTranslation()
  const [showModal, hideModal] = useModal(
    () => (
      <Modal>
        <div className='modal-error-body'>
          <h6 className='text-center'>{message}</h6>
          <div className='modal-btn-style'>
            <NeoButton
              tabIndex='1'
              mode='newDesign'
              type='button'
              text={t(BUTTON_KEYS.OK)}
              onClick={() => {
                hideModal()
              }}
            />
          </div>
        </div>
      </Modal>
    ),
    [message],
  )

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return <NeoSuccess message={message} linkPath='/transfers' />
      default:
        return renderForm()
    }
  }

  const onVerify = useCallback(
    async (data) => {
      try {
        if (data.transferAmount < config.MIN_TRANSFER_LIMIT) {
          return
        }
        setFormValues(data)
        setLoading(true)
        console.log(data)
        const response = await BeneficiaryService.verifyInternalBeneficiary(data)
        if (response.status === 200) {
          setOtpEnabled(response.data['otpEnable'])
          setVerifiedBeneDetails(response.data)
          setStage(2)
          setLoading(false)
        }
      } catch (err) {
        setLoading(false)
        setMessage(err?.data?.message)
        //showModal()
      }
    },
    [showModal],
  )
  const onConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const reqParams = {
        ...verifiedBeneDetails,
        ...formValues,
        otp: otp,
      }
      const response = await BeneficiaryService.confirmInternalBeneficiary(reqParams)
      if (response.status === 200) {
        setMessage(response.data['message'])
        setLoading(false)
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setMessage(err?.data?.message)
      setOtp('')
      //showModal()
    }
  }, [formValues, verifiedBeneDetails, otp])

  function renderForm() {
    return (
      <div>
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={AddInternalBeneficiarySchema}
          onSubmit={onVerify}>
          {({ isSubmitting, errors, touched, handleChange, values, setFieldValue }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='1'
                    showAsterisk={internalBeneConfig['isNickNameRequired']}
                    value={values.nickName}
                    text={t(ADD_BENEFICIARY_KEYS.NICK_NAME)}
                    name='nickName'
                    onChange={handleChange}
                    minLength={2}
                    maxLength={20}
                    onKeyPress={(event) => {
                      if (
                        !(event.charCode >= 65 && event.charCode <= 90) &&
                        !(event.charCode >= 97 && event.charCode <= 122) &&
                        event.charCode !== 32 &&
                        !(event.charCode >= 48 && event.charCode <= 57) &&
                        event.charCode !== 95
                      ) {
                        event.preventDefault()
                      }
                    }}
                  />
                  <InputError
                    text={errors.nickName}
                    onChange={handleChange}
                    touched={touched.nickName}
                  />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='2'
                    showAsterisk={internalBeneConfig['isAccountNoRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.ACCOUNT_NO)}
                    mode='password'
                    onChange={(value) => {
                      setFieldValue('accountNumber', value?.value)
                    }}
                    maxLength={config.ACC_NO_LENGTH}
                    name='accountNumber'
                  />
                  <InputError text={errors.accountNumber} touched={touched.accountNumber} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='3'
                    showAsterisk={internalBeneConfig['isConfirmAccountNoRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.CONFIRM_ACCOUNT_NO)}
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('confirmAccountNumber', value?.value)
                    }}
                    name='confirmAccountNumber'
                    maxLength={config.ACC_NO_LENGTH}
                  />
                  <InputError
                    text={errors.confirmAccountNumber}
                    touched={touched.confirmAccountNumber}
                  />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='4'
                    showAsterisk={internalBeneConfig['isTransferLimitRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.TRANSFER_LIMIT)}
                    type='number'
                    mode='amount'
                    value={values.maxTransferAmount}
                    onChange={(value) => {
                      setFieldValue('transferAmount', value?.floatValue)
                      setFieldValue('maxTransferAmount', value?.value)
                    }}
                    name='maxTransferAmount'
                  />
                  <InputError text={errors.maxTransferAmount} touched={touched.maxTransferAmount} />
                  <InputError
                    text={`${t(ADD_BENEFICIARY_KEYS.TRANSFER_LIMIT_TXT)} ${
                      config.MIN_TRANSFER_LIMIT
                    }.00`}
                    touched={values.transferAmount < config.MIN_TRANSFER_LIMIT}
                  />
                </div>
              </div>

              <div class='form-check'>
                <Field
                  tabIndex={'5'}
                  as='input'
                  id='visibleGroup'
                  type='checkbox'
                  className='form-check-input'
                  name='isVisibleToGroup'
                />
                <label class='form-check-label' for='visibleGroup'>
                  {t(ADD_BENEFICIARY_KEYS.VISIBLE_GROUP)}
                </label>
              </div>

              <div className='mt-3'>
                {loading ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    tabIndex='6'
                    type='submit'
                    text={t(BUTTON_KEYS.SUBMIT)}
                    disabled={isSubmitting}
                    mode='newDesign'
                  />
                )}
              </div>
              <div className='back-cancel-btn-container'>
                <button
                  tabIndex={'7'}
                  className='back-cancel-btn btn btn-sm new-design-warning-text-color'
                  onClick={() => history.goBack()}>
                  {' '}
                  {t(BUTTON_KEYS.CANCEL)}{' '}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderConfirmation() {
    return (
      <div className='d-flex flex-column confirmation'>
        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.NICK_NAME)}</h6>
          <h6 className='value'>{formValues.nickName}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.BENEFICIARY_NAME)}</h6>
          <h6 className='value'>{verifiedBeneDetails.beneName}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.ACCOUNT_NO)}</h6>
          <h6 className='value'>{formValues.accountNumber}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.BRANCH_CODE)}</h6>
          <h6 className='value'>{verifiedBeneDetails.beneBrn}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.BENEFICIARY_CURRENCY)}</h6>
          <h6 className='value'>{verifiedBeneDetails.beneCurrency}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.TRANSFER_LIMIT)}</h6>
          <h6 className='value'>
            <NumberFormat
              value={formValues.maxTransferAmount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={''}
              fixedDecimalScale={true}
              decimalScale={2}
              allowEmptyFormatting
            />
          </h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.VISIBLE_GROUP)}</h6>
          <h6 className='value'>{formValues.isVisibleToGroup ? 'Yes' : 'No'}</h6>
        </div>

        <div>
          <TermsAndCondition module='BENEFICIARY' />
        </div>

        <div className='mt-3'>
          {loading ? (
            renderLoading()
          ) : (
            <NeoButton
              type='button'
              mode='newDesign'
              className='btn btn-neo'
              onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
              text={t(BUTTON_KEYS.CONFIRM)}
            />
          )}
        </div>
        <div className='back-cancel-style' onClick={() => setStage(1)}>
          {t(BUTTON_KEYS.BACK)}{' '}
        </div>
      </div>
    )
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='BENE_INTERNAL'>
      <div className='new-design-form-style'>
        <div className='heading-style'>{t(ADD_BENEFICIARY_KEYS.INTERNAL_BENE_HEADER)}</div>
        {renderSwitch()}
      </div>
    </LayoutWithAccountCard>
  )
}
